const { stableStringify } = require('../export/json');

const HASH_ALGORITHM = 'SHA-256';

function createSessionId(now = new Date()) {
  return `rf-${now.toISOString().replace(/[^0-9]/g, '').slice(0, 14)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

function getTextEncoder() {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder();
  }

  try {
    const nodeRequire = eval('require');
    const { TextEncoder: NodeTextEncoder } = nodeRequire('util');
    return new NodeTextEncoder();
  } catch (error) {
    throw new Error('TextEncoder is required to create audit hashes.');
  }
}

function getSubtleCrypto() {
  if (globalThis.crypto && globalThis.crypto.subtle) {
    return globalThis.crypto.subtle;
  }

  try {
    const nodeRequire = eval('require');
    return nodeRequire('crypto').webcrypto.subtle;
  } catch (error) {
    return null;
  }
}

function legacyHashString(input) {
  let hash = 2166136261;

  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return `fnv1a-${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

async function hashString(input) {
  const subtle = getSubtleCrypto();

  if (!subtle) {
    return `sha256-unavailable-${legacyHashString(input)}`;
  }

  const encoder = getTextEncoder();
  const digest = await subtle.digest('SHA-256', encoder.encode(input));
  const hash = Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

  return `sha256-${hash}`;
}

function normalizeDetails(details = {}) {
  return JSON.parse(JSON.stringify(details));
}

class AuditTrail {
  constructor(options = {}) {
    this.sessionId = options.sessionId || createSessionId();
    this.events = [];
    this.pending = Promise.resolve();
  }

  record(action, details = {}) {
    const operation = this.pending.then(() => this.createEvent(action, details));
    this.pending = operation.catch(() => {});
    return operation;
  }

  async createEvent(action, details = {}) {
    const previousHash = this.events.length
      ? this.events[this.events.length - 1].eventHash
      : 'GENESIS';
    const event = {
      index: this.events.length + 1,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      action,
      hashAlgorithm: HASH_ALGORITHM,
      details: normalizeDetails(details),
      previousHash
    };

    event.eventHash = await hashString(`${previousHash}:${stableStringify(event)}`);
    this.events.push(Object.freeze(event));
    return event;
  }

  async flush() {
    await this.pending;
  }

  snapshot() {
    return {
      sessionId: this.sessionId,
      eventCount: this.events.length,
      chainHead: this.events.length ? this.events[this.events.length - 1].eventHash : 'GENESIS',
      events: this.events.map((event) => ({ ...event }))
    };
  }
}

function auditTrailToText(snapshot) {
  const rows = [
    'randFlow Audit Trail',
    `Session ID: ${snapshot.sessionId}`,
    `Event count: ${snapshot.eventCount}`,
    `Chain head: ${snapshot.chainHead}`,
    '',
    'Index, Timestamp, Action, Previous Hash, Event Hash, Details'
  ];

  snapshot.events.forEach((event) => {
    rows.push([
      event.index,
      event.timestamp,
      event.action,
      event.previousHash,
      event.eventHash,
      stableStringify(event.details)
    ].join(', '));
  });

  return rows.join('\n');
}

module.exports = {
  AuditTrail,
  HASH_ALGORITHM,
  auditTrailToText,
  createSessionId,
  hashString
};
